import { StateCreator } from 'zustand'
import { SharedSlice } from 'src/components/slice'
import { tenderService } from '$services'
import { exceptionMessageHandler } from '$utils'
import { TenderReview } from '@loadedhub/loaded-stock-api-sdk'
import { Dayjs } from 'dayjs'

export interface TenderReviewFilters {
  dateRange: [Dayjs | null, Dayjs | null]
  includeCredits: boolean
}

interface ReviewTenderState {
  tenderReview?: TenderReview
  reviewFilters: TenderReviewFilters
}

export interface ReviewTenderSlice extends ReviewTenderState {
  resetReviewTenderSlice: () => void
  loadTenderReview: (
    tenderId: string,
    startTime?: Dayjs,
    endTime?: Dayjs,
    includeCredits?: boolean
  ) => Promise<void>
  setReviewFilters: (filters: Partial<TenderReviewFilters>) => void
}

const initialReviewTenderState: ReviewTenderState = {
  tenderReview: undefined,
  reviewFilters: {
    dateRange: [null, null],
    includeCredits: true,
  },
}

const createReviewTenderSlice: StateCreator<
  ReviewTenderSlice & SharedSlice,
  [],
  [],
  ReviewTenderSlice
> = (set) => ({
  ...initialReviewTenderState,

  resetReviewTenderSlice: () => set(initialReviewTenderState),

  loadTenderReview: async (
    tenderId: string,
    startTime?: Dayjs,
    endTime?: Dayjs,
    includeCredits?: boolean
  ) => {
    try {
      const isInitialLoad = startTime === undefined && endTime === undefined

      let start = startTime
      let end = endTime
      let credits = includeCredits ?? true

      if (isInitialLoad) {
        const tender = await tenderService.getTender(tenderId)
        if (!tender.id) {
          throw exceptionMessageHandler(
            new Error('Tender not found'),
            'Tender not found'
          )
        }
        start = tender.datestampStart
        end = tender.datestampEnd
        credits = true

        set({
          reviewFilters: {
            dateRange: [start ?? null, end ?? null],
            includeCredits: credits,
          },
        })
      }

      const tenderReview = await tenderService.reviewTender(
        tenderId,
        start,
        end,
        credits
      )

      set({ tenderReview })
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to load tender review')
    }
  },

  setReviewFilters: (filters: Partial<TenderReviewFilters>) =>
    set((state) => ({
      reviewFilters: { ...state.reviewFilters, ...filters },
    })),
})

export default createReviewTenderSlice
